import { CircleArrowDown, CircleArrowUp } from "lucide-react"
import { memo, useCallback } from "react"

import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { SimpleTooltip } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

interface ITransactionTypeSelectorProps {
  value: "income" | "outcome"
  onChange: (value: "income" | "outcome") => void
}

/**
 * Transaction type selector component (Income vs Expense)
 * Memoized to prevent unnecessary re-renders
 */
export const TransactionTypeSelector = memo(function TransactionTypeSelector({
  value,
  onChange,
}: ITransactionTypeSelectorProps) {
  const handleValueChange = useCallback(
    (newValue: string) => {
      onChange(newValue as "income" | "outcome")
    },
    [onChange],
  )

  return (
    <div className="space-y-2">
      <Label>Tipo</Label>
      <RadioGroup
        value={value}
        onValueChange={handleValueChange}
        className="flex items-center gap-0"
      >
        <RadioGroupItem value="outcome" id="outcome" className="hidden" />
        <SimpleTooltip title="Despesa" asChild>
          <Label
            htmlFor="outcome"
            className={cn(
              "bg-transparent dark:bg-input/30 flex border border-input h-9 w-full rounded-l-md justify-center cursor-pointer shadow-xs transition-[color,box-shadow]",
              value === "outcome" &&
                "bg-rose-500 dark:bg-rose-500 text-shadow-black text-shadow-2xs",
            )}
          >
            <CircleArrowUp className="size-4" />
          </Label>
        </SimpleTooltip>
        <RadioGroupItem value="income" id="income" className="hidden" />
        <SimpleTooltip title="Receita" asChild>
          <Label
            htmlFor="income"
            className={cn(
              "bg-transparent dark:bg-input/30 flex border border-input h-9 w-full rounded-r-md justify-center cursor-pointer shadow-xs transition-[color,box-shadow]",
              value === "income" &&
                "bg-emerald-500 dark:bg-emerald-500 text-shadow-black text-shadow-2xs",
            )}
          >
            <CircleArrowDown className="size-4" />
          </Label>
        </SimpleTooltip>
      </RadioGroup>
    </div>
  )
})

TransactionTypeSelector.displayName = "TransactionTypeSelector"
